import React from "react";
import Measure from "react-measure";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import { Popover } from "@vitalsource/vstui/labs";
import { Button } from "@vitalsource/vstui";
import BookStore from "../../../stores/book-store";
import Dispatcher from "../../../dispatcher";
import PrintRange from "./print-range";
import PrintRangeIcon from "./printer-icon";
import Spinner from "../../../ui/components/spinner";

const LoadingContainer = styled.div`
  width: 400px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinnerStyle = {
  position: "relative"
};

class PrintRangeToggleMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      waitingRequest: 2,
      copies: [],
      lifetimePrintPageCount: 0,
      totalPagesPrinted: 0,
      lifetimePrintLimit: 100,
      printLimit: -1,
      open: false
    };

    this.fetchLimits = this.fetchLimits.bind(this);
    this.fetchLifetimePages = this.fetchLifetimePages.bind(this);
    this.fetchPrintLimits = this.fetchPrintLimits.bind(this);
  }

  componentDidMount() {
    Dispatcher.bind(Dispatcher.BOOK_OPENED, this.fetchLimits);
    Dispatcher.bind(Dispatcher.PRINT_DONE, this.fetchLimits);
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_OPENED, this.fetchLimits);
    Dispatcher.unbind(Dispatcher.PRINT_DONE, this.fetchLimits);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.open !== nextState.open ||
      this.state.waitingRequest !== nextState.waitingRequest ||
      nextProps.currentPage !== this.props.currentPage ||
      this.state.lifetimePrintPageCount !== nextState.lifetimePrintPageCount ||
      this.state.totalPagesPrinted !== nextState.totalPagesPrinted
    );
  }

  fetchLimits() {
    this.fetchLifetimePages();
    this.fetchPrintLimits();
  }

  fetchPrintLimits() {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.getPrintLimits(response => {
      if (response.isSuccess() && response.data) {
        const {
          lifetimePrintLimit,
          totalPageBreaks,
          printLimit,
          totalPagesPrinted
        } = response.data;

        this.setState(state => ({
          lifetimePrintLimit,
          lifetimePrintPageCount: Math.round(
            (lifetimePrintLimit * totalPageBreaks) / 100
          ),
          printLimit: printLimit,
          totalPagesPrinted: totalPagesPrinted,
          waitingRequest:
            state.waitingRequest > 0 ? state.waitingRequest - 1 : 0
        }));
      }
    });
  }

  fetchLifetimePages() {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.getLifetimePages(response => {
      if (response.isSuccess() && response.data) {
        this.setState(state => ({
          copies: response.data.reduce((acc, page) => {
            acc[page.label] = page;
            return acc;
          }, {}),
          waitingRequest:
            state.waitingRequest > 0 ? state.waitingRequest - 1 : 0
        }));
      }
    });
  }

  // No timeout causes flickering in firefox
  handleFocus = () => {
    clearTimeout(this.timeoutId);
  };

  handleBlur = () => {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      if (this.state.open) {
        this.setState({ open: false });
      }
    }, 0);
  };

  render() {
    const { id, isbn, currentPage, renderDisabledButton } = this.props;
    const {
      waitingRequest,
      totalPagesPrinted,
      lifetimePrintLimit,
      printLimit,
      lifetimePrintPageCount,
      copies,
      open
    } = this.state;

    if (printLimit === 0) {
      return renderDisabledButton(i18n.t("reader.buttons.print-unavailable"));
    }

    // Measure will update the styles and re-render popover on content size change
    return (
      <Measure bounds>
        {({ measureRef }) => (
          <Popover
            open={open}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            content={
              <div ref={measureRef}>
                {waitingRequest > 0 ? (
                  <LoadingContainer>
                    <Spinner styles={SpinnerStyle} />
                  </LoadingContainer>
                ) : (
                  <PrintRange
                    isOpen={open}
                    isbn={isbn}
                    currentPage={currentPage}
                    totalPagesPrinted={totalPagesPrinted}
                    printLimit={printLimit}
                    lifetimePrintLimit={lifetimePrintLimit}
                    lifetimePrintPageCount={lifetimePrintPageCount}
                    copies={copies}
                  />
                )}
              </div>
            }
          >
            <Button
              id={id}
              iconStart={<PrintRangeIcon />}
              className="scrubber-button"
              label={
                totalPagesPrinted >= lifetimePrintPageCount
                  ? i18n.t("print.print-limit-reached")
                  : i18n.t("print.printing-available")
              }
              onClick={() => this.setState(state => ({ open: !state.open }))}
              naked
            />
          </Popover>
        )}
      </Measure>
    );
  }
}

PrintRangeToggleMenu.propTypes = {
  isbn: PropTypes.string.isRequired,
  id: PropTypes.string,
  currentPage: PropTypes.string,
  disabledButton: PropTypes.node
};

export default withTheme(PrintRangeToggleMenu);
